/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useState } from 'react'
import { css } from '@emotion/react'

import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiSplitPanel,
  EuiIcon,
  EuiText,
  EuiSkeletonText,
  EuiSpacer,
  EuiLink,
  useEuiTheme,
  EuiPanel,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { useListHostedTrafficFiltersQuery } from '@modules/network-security-lib/hooks/hosted'
import {
  useListServerlessTrafficFiltersQuery,
  useListAssociableProjects,
} from '@modules/network-security-lib/hooks/serverless'
import type { TrafficFilterRulesetInfo } from '@modules/cloud-api/v1/types'
import type { TrafficFilterInfo } from '@modules/network-user-api/v1/types'

import ProtectedResourcesModal from '../ProtectedResources/Modal'

const TrafficFilterSummary: React.FunctionComponent = () => {
  const { euiTheme } = useEuiTheme()

  const { data: hostedData, isLoading: isHostedLoading } = useListHostedTrafficFiltersQuery()
  const { data: serverlessData, isLoading: isServerlessLoading } =
    useListServerlessTrafficFiltersQuery()
  const { projects } = useListAssociableProjects()

  const isLoading = isHostedLoading || isServerlessLoading

  const [modalRulesets, setModalRulesets] = useState<{
    hosted?: TrafficFilterRulesetInfo[]
    serverless?: TrafficFilterInfo[]
  } | null>(null)

  const hostedRulesets = hostedData?.rulesets || []
  const serverlessRulesets = serverlessData?.items || []

  const hostedIpFilters = hostedRulesets.filter((ruleset) => ruleset.type === 'ip')
  const serverlessIpFilters = serverlessRulesets.filter((ruleset) => ruleset.type === 'ip')

  const associatedHostedIpFilters = hostedIpFilters.filter((ruleset) =>
    Boolean(ruleset.associations?.length),
  )
  const associatedServerlessIpFilters = serverlessIpFilters.filter((ruleset) =>
    projects.some((project) => project.traffic_filters?.some(({ id }) => id === ruleset.id)),
  )

  const allIpFilters = [...hostedIpFilters, ...serverlessIpFilters]
  const associatedIpFilters = [...associatedHostedIpFilters, ...associatedServerlessIpFilters]

  const associatedIpFilterDeploymentIds = new Set<string>(
    associatedHostedIpFilters.flatMap(
      (ruleset) =>
        ruleset.associations
          ?.filter((association) => association.entity_type === 'deployment')
          .map((association) => association.id) || [],
    ),
  )
  const associatedIpFilterProjectIds = new Set<string>(
    projects.flatMap((project) =>
      project.traffic_filters?.some(({ id: rulesetId }) =>
        serverlessIpFilters.some(({ id }) => id === rulesetId),
      )
        ? [project.id]
        : [],
    ),
  )

  const hostedPrivateConnections = hostedRulesets.filter((ruleset) => ruleset.type !== 'ip')
  const serverlessPrivateConnections = serverlessRulesets.filter((ruleset) => ruleset.type !== 'ip')

  const associatedHostedPrivateConnections = hostedPrivateConnections.filter((ruleset) =>
    Boolean(ruleset.associations?.length),
  )
  const associatedServerlessPrivateConnections = serverlessPrivateConnections.filter((ruleset) =>
    projects.some((project) => project.traffic_filters?.some(({ id }) => id === ruleset.id)),
  )

  const allPrivateConnections = [...hostedPrivateConnections, ...serverlessPrivateConnections]
  const associatedPrivateConnections = [
    ...associatedHostedPrivateConnections,
    ...associatedServerlessPrivateConnections,
  ]

  const associatedPrivateConnectionDeploymentIds = new Set<string>(
    associatedHostedPrivateConnections.flatMap(
      (ruleset) =>
        ruleset.associations
          ?.filter((association) => association.entity_type === 'deployment')
          .map((association) => association.id) || [],
    ),
  )
  const associatedPrivateConnectionProjectIds = new Set<string>(
    serverlessPrivateConnections.flatMap((ruleset) =>
      projects.some((project) => project.traffic_filters?.some(({ id }) => id === ruleset.id))
        ? [ruleset.id]
        : [],
    ),
  )

  const rightBorderStyle = css`
    border-right: 1px solid ${euiTheme.colors.borderBasePlain};
    padding-right: ${euiTheme.size.m};
  `

  const ipFilterContent = isLoading ? (
    <EuiSkeletonText lines={2} />
  ) : (
    <EuiFlexGroup gutterSize='m' alignItems='flexStart'>
      <EuiFlexItem>
        <EuiPanel paddingSize='none' color='transparent' css={rightBorderStyle}>
          <EuiText>
            <strong>
              <FormattedMessage
                id='networkSecurity.ipFiltersSummary.totalIpFilters'
                defaultMessage='{count} {count, plural, one {IP filter} other {IP filters}}'
                values={{ count: allIpFilters.length }}
              />
            </strong>
          </EuiText>
          <EuiText size='s'>
            <FormattedMessage
              id='networkSecurity.ipFiltersSummary.associatedIpFilters'
              defaultMessage='{associatedCount} of {totalCount} in use'
              values={{
                associatedCount: associatedIpFilters.length,
                totalCount: allIpFilters.length,
              }}
            />
          </EuiText>
        </EuiPanel>
      </EuiFlexItem>
      <EuiFlexItem>
        <EuiSpacer size='xs' />
        <EuiText size='s' color='subdued'>
          <strong>
            <FormattedMessage
              id='networkSecurity.ipFiltersSummary.protectedResources'
              defaultMessage='Protected resources'
            />
          </strong>
        </EuiText>

        <EuiFlexGroup gutterSize='m' alignItems='flexStart'>
          <EuiFlexItem grow={false}>
            <EuiText size='s'>
              <EuiLink
                disabled={associatedIpFilterDeploymentIds.size === 0}
                onClick={() => {
                  setModalRulesets({ hosted: associatedHostedIpFilters })
                }}
              >
                <FormattedMessage
                  id='networkSecurity.ipFiltersSummary.associatedDeployments'
                  defaultMessage='{count} {count, plural, one {deployment} other {deployments}}'
                  values={{ count: associatedIpFilterDeploymentIds.size }}
                />
              </EuiLink>
            </EuiText>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiText size='s'>
              <EuiLink
                disabled={associatedIpFilterProjectIds.size === 0}
                onClick={() => {
                  setModalRulesets({ serverless: associatedServerlessIpFilters })
                }}
              >
                <FormattedMessage
                  id='networkSecurity.ipFiltersSummary.associatedProjects'
                  defaultMessage='{count} {count, plural, one {project} other {projects}}'
                  values={{ count: associatedIpFilterProjectIds.size }}
                />
              </EuiLink>
            </EuiText>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlexItem>
    </EuiFlexGroup>
  )

  const privateConnectionContent = isLoading ? (
    <EuiSkeletonText lines={2} />
  ) : (
    <EuiFlexGroup gutterSize='m' alignItems='flexStart'>
      <EuiFlexItem>
        <EuiPanel paddingSize='none' color='transparent' css={rightBorderStyle}>
          <EuiText>
            <strong>
              <FormattedMessage
                id='networkSecurity.privateConnectionsSummary.totalPrivateConnections'
                defaultMessage='{count} {count, plural, one {Private connection} other {Private connections}}'
                values={{ count: allPrivateConnections.length }}
              />
            </strong>
          </EuiText>
          <EuiText size='s'>
            <FormattedMessage
              id='networkSecurity.privateConnectionsSummary.associated'
              defaultMessage='{associatedCount} of {totalCount} in use'
              values={{
                associatedCount: associatedPrivateConnections.length,
                totalCount: allPrivateConnections.length,
              }}
            />
          </EuiText>
        </EuiPanel>
      </EuiFlexItem>
      <EuiFlexItem css={rightBorderStyle}>
        <EuiSpacer size='xs' />
        <EuiText size='s' color='subdued'>
          <strong>
            <FormattedMessage
              id='networkSecurity.privateConnectionsSummary.protectedResources'
              defaultMessage='Protected resources'
            />
          </strong>
        </EuiText>
        <EuiFlexGroup gutterSize='m' alignItems='flexStart'>
          <EuiFlexItem grow={false}>
            <EuiText size='s'>
              <EuiLink
                disabled={associatedPrivateConnectionDeploymentIds.size === 0}
                onClick={() => {
                  setModalRulesets({ hosted: associatedHostedPrivateConnections })
                }}
              >
                <FormattedMessage
                  id='networkSecurity.privateConnectionsSummary.associatedDeployments'
                  defaultMessage='{count} {count, plural, one {deployment} other {deployments}}'
                  values={{ count: associatedPrivateConnectionDeploymentIds.size }}
                />
              </EuiLink>
            </EuiText>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiText size='s'>
              <EuiLink
                disabled={associatedPrivateConnectionProjectIds.size === 0}
                onClick={() => {
                  setModalRulesets({ serverless: associatedServerlessPrivateConnections })
                }}
              >
                <FormattedMessage
                  id='networkSecurity.privateConnectionsSummary.associatedProjects'
                  defaultMessage='{count} {count, plural, one {project} other {projects}}'
                  values={{ count: associatedPrivateConnectionProjectIds.size }}
                />
              </EuiLink>
            </EuiText>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlexItem>
    </EuiFlexGroup>
  )

  return (
    <Fragment>
      <EuiFlexGroup gutterSize='m' alignItems='stretch'>
        <EuiFlexItem data-test-subj='ipFiltersSummary'>
          <EuiSplitPanel.Outer hasBorder={true}>
            <EuiSplitPanel.Inner grow={false} color='subdued' paddingSize='s'>
              <EuiFlexGroup gutterSize='s' alignItems='center'>
                <EuiFlexItem grow={false}>
                  <EuiIcon type='logstashFilter' />
                </EuiFlexItem>
                <EuiFlexItem>
                  <EuiText size='s'>
                    <FormattedMessage
                      id='networkSecurity.ipFiltersSummary.title'
                      defaultMessage='IP filters usage summary'
                    />
                  </EuiText>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiSplitPanel.Inner>
            <EuiSplitPanel.Inner paddingSize='m'>
              <EuiFlexGroup gutterSize='m' alignItems='stretch'>
                <EuiFlexItem>{ipFilterContent}</EuiFlexItem>
              </EuiFlexGroup>
            </EuiSplitPanel.Inner>
          </EuiSplitPanel.Outer>
        </EuiFlexItem>
        <EuiFlexItem data-test-subj='privateConnectionsSummary'>
          <EuiSplitPanel.Outer hasBorder={true}>
            <EuiSplitPanel.Inner grow={false} color='subdued' paddingSize='s'>
              <EuiFlexGroup gutterSize='s' alignItems='center'>
                <EuiFlexItem grow={false}>
                  <EuiIcon type='lock' />
                </EuiFlexItem>
                <EuiFlexItem>
                  <EuiText size='s'>
                    <FormattedMessage
                      id='networkSecurity.privateConnectionsSummary.title'
                      defaultMessage='Private connections usage summary'
                    />
                  </EuiText>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiSplitPanel.Inner>
            <EuiSplitPanel.Inner paddingSize='m'>
              <EuiFlexGroup gutterSize='m' alignItems='stretch'>
                <EuiFlexItem>{privateConnectionContent}</EuiFlexItem>
              </EuiFlexGroup>
            </EuiSplitPanel.Inner>
          </EuiSplitPanel.Outer>
        </EuiFlexItem>
      </EuiFlexGroup>

      {modalRulesets && (
        <ProtectedResourcesModal
          rulesets={modalRulesets}
          onClose={() => {
            setModalRulesets(null)
          }}
        />
      )}
    </Fragment>
  )
}

export default TrafficFilterSummary
